import { useMutation, useQueryClient } from '@tanstack/react-query'

import { apiErrorMessage } from '@/lib/api-error-message'
import { policyCatalogKeys, policyDefinitionKeys, policyOrderKeys, policyResultKeys } from '@/modules/hierarchy'
import { PREFERRED_COUNTER_POLICY_TYPE, preferredCounterPolicyApi, type PreferredCounterPolicy, type PreferredCounterEdit } from '../../api/preferred-counter-policy-api'
import { usePrinterPolicyReconciliation } from '../printer-policies/use-printer-policy-reconciliation'

export function useSavePreferredCounterPolicy(entityId: string) {
  const client = useQueryClient()
  const refreshKeys = [policyCatalogKeys.all(entityId), policyOrderKeys.type(entityId, PREFERRED_COUNTER_POLICY_TYPE),
    policyDefinitionKeys.all(entityId), policyResultKeys.type(entityId, PREFERRED_COUNTER_POLICY_TYPE)]
  return useMutation({
    mutationKey: ['printer-policy-save', entityId, PREFERRED_COUNTER_POLICY_TYPE],
    mutationFn: ({ policy, edit, operationId }: { policy: PreferredCounterPolicy | null; edit: PreferredCounterEdit; operationId: string }) => policy
      ? preferredCounterPolicyApi.update(entityId, policy, edit)
      : preferredCounterPolicyApi.create(entityId, edit, operationId),
    onSuccess: async () => {
      await Promise.all(refreshKeys.map((queryKey) => client.cancelQueries({ queryKey })))
      await Promise.all(refreshKeys.map((queryKey) => client.invalidateQueries({ queryKey })))
    },
  })
}

export function useDeletePreferredCounterPolicy(entityId: string) {
  const deletion = usePrinterPolicyReconciliation(entityId, PREFERRED_COUNTER_POLICY_TYPE)
  return useMutation({
    mutationKey: ['printer-policy-delete', entityId, PREFERRED_COUNTER_POLICY_TYPE],
    mutationFn: (policy: Pick<PreferredCounterPolicy, 'id' | 'revision'>) => preferredCounterPolicyApi.delete(entityId, policy),
    retry: false,
    onSuccess: (_, policy) => deletion.deleted(policy.id),
  })
}

export function preferredCounterPolicyError(error: unknown, operation: 'save' | 'load' = 'save') {
  const fallback = {
    save: 'Couldn’t save the preferred counters policy. Your edits are kept. Try again.',
    load: 'Couldn’t load the preferred counters policy. Your edits are kept. Try reloading again.',
  }[operation]
  return apiErrorMessage(error, fallback, {
    codes: {
      POLICY_ORDER_CONFLICT: 'The policy order changed. Reload the order, review the position, and save again.',
      PRINTER_POLICY_CONFLICT: 'The request conflicts with the saved policy or an earlier operation. Your edits are kept. Review the latest policy before saving again.',
      INVALID_PRINTER_POLICY: 'Check the policy values and selector before saving again.',
      PRINTER_POLICY_EVALUATOR_UNAVAILABLE: 'Policy validation is unavailable. Try again shortly.',
      PRINTER_POLICY_NOT_FOUND: 'This policy no longer exists. Close the editor and refresh the list.',
    },
  })
}
